<template>
  <div id="parceria-view">
    <Breadcrumb :items="breadcrumbItems" />

    <div v-if="carregando">
      <Carregamento />
    </div>

    <div v-else-if="erro" class="mensagem-erro">
      <h2>Não foi possível carregar a solicitação</h2>

      <p>
        {{ erro }}
      </p>

      <button class="btn-voltar" type="button" @click="voltarParaParcerias">
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        Voltar
      </button>
    </div>

    <div v-else-if="parceria" class="formulario-container">

      <header class="cabecalho">
        <div class="cabecalho-informacoes">
          <span class="tag">
            SOLICITAÇÃO DE PARCERIA
          </span>

          <h1>
            {{ valor(parceria.nomeAcao, "Solicitação de parceria") }}
          </h1>

          <p>
            {{
              valor(
                parceria.tituloAcao,
                "Detalhes da solicitação de parceria"
              )
            }}
          </p>

          <span class="tempo">
            Solicitação realizada em
            {{ formatarDataHora(parceria.dataSolicitacao) }}
          </span>
        </div>

        <div class="status-container">
          <span class="status-label">
            STATUS DA SOLICITAÇÃO:
          </span>

          <span class="status" :class="classeStatus(parceria.status)">
            {{ traduzirStatus(parceria.status) }}
          </span>
        </div>
      </header>
      <div v-if="feedback.texto" class="feedback" :class="feedback.tipo">
        {{ feedback.texto }}

        <button type="button" aria-label="Fechar mensagem" @click="limparFeedback">
          ×
        </button>
      </div>

      <div
        v-if="parceria.justificativaStatus && (statusAtual === STATUS_PARCERIA.REJEITADA || statusAtual === STATUS_PARCERIA.CANCELADA)"
        class="bloco-justificativa-status">
        <strong>
          {{ statusAtual === STATUS_PARCERIA.CANCELADA ? "Motivo do cancelamento:" : "Motivo do deferimento:" }}
        </strong>
        <p>{{ parceria.justificativaStatus }}</p>
      </div>

      <div class="progresso-container">
        <div class="progresso-topo">
          <span>
            Etapa {{ etapaAtual }} de {{ totalEtapas }}
          </span>

          <span>
            {{ progresso }}%
          </span>
        </div>

        <div class="barra-progresso">
          <div class="barra-progresso-preenchida" :style="{ width: `${progresso}%` }"></div>
        </div>

        <div class="etapas">
          <div v-for="etapa in totalEtapas" :key="etapa" class="etapa" :class="{
            ativa: etapa === etapaAtual,
            concluida: etapa < etapaAtual,
          }">
            <span>
              {{ etapa }}
            </span>

            <small>
              {{ nomesEtapas[etapa - 1] }}
            </small>
          </div>
        </div>
      </div>


      <section v-if="etapaAtual === 1" class="etapa-conteudo">
        <div class="secao-titulo">
          <span>SEÇÃO 1</span>

          <h2>
            Identificação da solicitação
          </h2>

          <p>
            Informações gerais sobre a solicitação de parceria.
          </p>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Nome da ação" :valor="parceria.nomeAcao" />

          <CampoVisualizacao label="Título da ação" :valor="parceria.tituloAcao" />

          <CampoVisualizacao label="Tipos de ação" :valor="formatarLista(parceria.tiposAcao)" />

          <CampoVisualizacao label="Área temática" :valor="parceria.areaTematica" />

          <CampoVisualizacao label="Tipo de parceria" :valor="traduzirTipoParceria(parceria.tipoParceria)" />

          <CampoVisualizacao label="Status" :valor="traduzirStatus(parceria.status)" />

          <CampoVisualizacao label="Data da solicitação" :valor="formatarDataHora(parceria.dataSolicitacao)" />

          <CampoVisualizacao label="Declaração" :valor="parceria.declaracao ? 'Sim' : 'Não'" />

          <CampoVisualizacao label="Unidade / Curso" :valor="parceria.unidadeCurso" />

          <CampoVisualizacao label="Campus" :valor="parceria.campus" />

          <CampoVisualizacao label="SIGAA" :valor="parceria.sigaa" />
        </div>
      </section>

      <section v-if="etapaAtual === 2" class="etapa-conteudo">
        <div class="secao-titulo">
          <span>SEÇÃO 2</span>

          <h2>
            Dados do responsável
          </h2>

          <p>
            Informações do responsável pela solicitação.
          </p>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Nome" :valor="parceria.nome" />

          <CampoVisualizacao label="SIAPE" :valor="parceria.siape" />

          <CampoVisualizacao label="E-mail" :valor="parceria.email" />

          <CampoVisualizacao label="Telefone" :valor="parceria.telefone" />
        </div>
      </section>

      <section v-if="etapaAtual === 3" class="etapa-conteudo">
        <div class="secao-titulo">
          <span>SEÇÃO 3</span>

          <h2>
            Informações da parceria
          </h2>

          <p>
            Dados relacionados à parceria e à organização envolvida.
          </p>
        </div>

        <div class="bloco-destaque">
          <span>
            TIPO DE PARCERIA
          </span>

          <strong>
            {{ traduzirTipoParceria(parceria.tipoParceria) }}
          </strong>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Tipos de parceiro" :valor="formatarLista(parceria.tiposParceiro)" />

          <CampoVisualizacao label="Campi parceiros" :valor="formatarLista(parceria.campiParceiros)" />

          <CampoVisualizacao label="Unidade parceira" :valor="parceria.unidadeParceira" />

          <CampoVisualizacao label="Contato da unidade" :valor="parceria.contatoUnidade" />

          <CampoVisualizacao label="Pessoa de contato" :valor="parceria.pessoaContato" />
        </div>

        <template v-if="parceria.tipoParceria === 'EXTERNA'">
          <div class="subsecao">
            <h3>
              Dados da parceria externa
            </h3>
          </div>

          <div class="campos-grid">
            <CampoVisualizacao label="Entidade / organização" :valor="parceria.entidadeVista" />

            <CampoVisualizacao label="Contato da entidade" :valor="parceria.contatoEntidade" />

            <CampoVisualizacao label="Nome da entidade" :valor="parceria.nomeEntidade" />

            <CampoVisualizacao label="CNPJ" :valor="parceria.cnpj" />

            <CampoVisualizacao label="Natureza jurídica" :valor="parceria.naturezaJuridica" />

            <CampoVisualizacao label="Telefone de contato" :valor="parceria.telefoneContato" />

            <CampoVisualizacao label="Município / Estado" :valor="parceria.municipioEstado" />
          </div>
        </template>

        <template v-else-if="
          parceria.tipoParceria === 'INTRA_MUROS' ||
          parceria.tipoParceria === 'INTERNA'
        ">
          <div class="subsecao">
            <h3>
              Dados da parceria interna
            </h3>
          </div>

          <div class="campos-grid">
            <CampoVisualizacao label="Campi parceiros" :valor="formatarLista(parceria.campiParceiros)" />

            <CampoVisualizacao label="Unidade parceira" :valor="parceria.unidadeParceira" />

            <CampoVisualizacao label="Contato da unidade" :valor="parceria.contatoUnidade" />

            <CampoVisualizacao label="Pessoa de contato" :valor="parceria.pessoaContato" />
          </div>
        </template>

        <div class="subsecao">
          <h3>
            Articulação da parceria
          </h3>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Tipos de articulação" :valor="formatarLista(parceria.tiposArticulacao)" />

          <CampoVisualizacao label="Protagonismo da comunidade" :valor="parceria.protagonismoComunidade" />

          <CampoVisualizacao label="Como conheceu a oportunidade" :valor="parceria.comoConheceu" />
        </div>
      </section>
      <section v-if="etapaAtual === 4" class="etapa-conteudo">
        <div class="secao-titulo">
          <span>SEÇÃO 4</span>

          <h2>
            Detalhamento da proposta
          </h2>

          <p>
            Informações sobre a proposta, problema, benefícios e público
            beneficiado.
          </p>
        </div>

        <CampoTexto label="Proposta" :valor="parceria.proposta" />

        <CampoTexto label="Resumo da proposta" :valor="parceria.resumoProposta" />

        <CampoTexto label="Problema" :valor="parceria.problema" />

        <CampoTexto label="Benefícios" :valor="parceria.beneficios" />

        <div class="subsecao">
          <h3>
            Beneficiários
          </h3>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Beneficiários" :valor="parceria.beneficiarios" classe="campo-longo" />

          <CampoVisualizacao label="Quantidade de beneficiários" :valor="parceria.quantidadeBeneficiarios" />

          <CampoVisualizacao label="Abrangência" :valor="parceria.abrangencia" />

          <CampoVisualizacao label="Grupos em situação de vulnerabilidade"
            :valor="formatarLista(parceria.gruposVulnerabilidade)" classe="campo-longo" />
        </div>

        <div class="subsecao">
          <h3>
            Contrapartidas
          </h3>
        </div>

        <CampoTexto label="Contrapartidas oferecidas" :valor="parceria.contrapartidasOferecidas" />

        <CampoTexto label="Contrapartidas esperadas" :valor="formatarLista(parceria.contrapartidasEsperadas)" />
      </section>

      <section v-if="etapaAtual === 5" class="etapa-conteudo">
        <div class="secao-titulo">
          <span>SEÇÃO 5</span>

          <h2>
            Execução e avaliação
          </h2>

          <p>
            Informações sobre a execução da parceria e sua avaliação.
          </p>
        </div>

        <div class="campos-grid">
          <CampoVisualizacao label="Duração" :valor="parceria.duracao" />

          <CampoVisualizacao label="Palavras-chave" :valor="formatarLista(parceria.palavrasChave)" />

          <CampoVisualizacao label="Participação dos estudantes" :valor="parceria.participacaoEstudantes" />

          <CampoVisualizacao label="Indicação docente" :valor="parceria.indicacaoDocente" />
        </div>

        <CampoTexto label="Observações" :valor="parceria.observacoes" />
      </section>

      <div class="navegacao">
        <button v-if="etapaAtual > 1" class="btn-voltar" type="button" @click="voltar">
          <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
          Voltar
        </button>

        <button type="button" class="btn-historico" @click="abrirHistorico">
          <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
          Progresso da solicitação
        </button>

        <!-- Ações da PREX -->

        <!-- Formulário recebido → Em análise -->
        <button v-if="ehPREX && statusAtual === STATUS_PARCERIA.FORMULARIO_RECEBIDO" type="button"
          class="btn-analisar-prex" :disabled="processandoDecisao" @click="solicitarSenha('analisar')">
          <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
          Analisar
        </button>

        <!-- Em análise → Em articulação e formalização -->
        <button v-if="ehPREX && statusAtual === STATUS_PARCERIA.EM_ANALISE" type="button" class="btn-aprovar-prex"
          :disabled="processandoDecisao" @click="solicitarSenha('articular')">
          <i class="fa-solid fa-handshake" aria-hidden="true"></i>
          Em articulação e formalização
        </button>

        <!-- Em articulação → Aguardando aprovação da reitoria -->
        <button v-if="ehPREX && statusAtual === STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO" type="button"
          class="btn-aprovar-prex" :disabled="processandoDecisao" @click="solicitarSenha('reitoria')">
          <i class="fa-solid fa-building-columns" aria-hidden="true"></i>
          Aguardando aprovação da reitoria
        </button>

        <!-- Formalizar parceria -->
        <button v-if="ehPREX && podeFormalizar" type="button" class="btn-aprovar-prex btn-formalizar-destaque"
          :disabled="processandoDecisao" @click="abrirAprovacao">
          <i class="fa-solid fa-check" aria-hidden="true"></i>
          Formalizar parceria
        </button>

        <!-- Rejeitar parceria antes da formalização -->
        <button v-if="ehPREX && podeRejeitar" type="button" class="btn-rejeitar-prex" :disabled="processandoDecisao"
          @click="abrirDecisaoRejeitar">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          Indeferir parceria
        </button>

        <!-- Cancelar parceria após a formalização -->
        <button v-if="ehPREX && statusAtual === STATUS_PARCERIA.PARCERIA_FORMALIZADA" type="button"
          class="btn-rejeitar-prex" :disabled="processandoDecisao" @click="abrirDecisaoCancelar">
          <i class="fa-solid fa-ban" aria-hidden="true"></i>
          Cancelar parceria
        </button>

        <!-- Ações do coordenador -->

        <button v-if="!ehPREX && podeEditarCoordenador" type="button" class="btn-editar-coordenador"
          :disabled="processandoDecisao" @click="abrirConfirmacaoEdicaoCoordenador">
          <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
          Editar
        </button>

        <button v-if="!ehPREX && statusAtual === STATUS_PARCERIA.EM_EDICAO" type="button" class="btn-aprovar-prex"
          :disabled="processandoDecisao" @click="enviarSolicitacaoCoordenador">
          <i class="fa-solid fa-paper-plane" aria-hidden="true"></i>
          Enviar para análise
        </button>

        <button v-if="!ehPREX && podeEditarCoordenador" type="button" class="btn-rejeitar-prex"
          :disabled="processandoDecisao" @click="excluirSolicitacaoCoordenador">
          <i class="fa-solid fa-trash" aria-hidden="true"></i>
          Excluir solicitação
        </button>

        <!-- Navegação entre etapas -->

        <button v-if="etapaAtual < totalEtapas" class="btn-continuar" type="button" @click="avancar">
          Continuar
          <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
        </button>

        <button v-else class="btn-enviar" type="button" @click="voltarParaParcerias">
          Voltar para parcerias
        </button>
      </div>
    </div>


    <div v-if="modalHistorico.aberto" class="overlay" @click.self="fecharHistorico">
      <div class="modal-historico">
        <div class="modal-header">
          <span class="modal-label">Progresso da solicitação</span>

          <button type="button" class="btn-fechar-modal" aria-label="Fechar histórico" @click="fecharHistorico">
            ×
          </button>
        </div>

        <div class="historico-body">
          <div v-if="parceria?.avaliacoes?.length" class="linha-do-tempo">
            <article v-for="(avaliacao, index) in parceria.avaliacoes" :key="avaliacao.id || index"
              class="historico-item">
              <div class="historico-icone" :class="classeIconeHistorico(avaliacao.decisaoTomada)">
                <i :class="iconeHistorico(avaliacao.decisaoTomada)" aria-hidden="true"></i>
              </div>

              <div class="historico-conteudo">
                <div class="historico-topo">
                  <div>
                    <span class="historico-etapa">
                      AÇÃO {{ index + 1 }}
                    </span>

                    <h4>
                      {{ tituloHistorico(avaliacao.decisaoTomada) }}
                    </h4>
                  </div>

                  <span class="historico-data">
                    <i class="fa-regular fa-calendar" aria-hidden="true"></i>

                    {{ formatarDataHora(avaliacao.dataAvaliacao) }}
                  </span>
                </div>

                <div class="historico-avaliador">
                  <i class="fa-solid fa-user" aria-hidden="true"></i>

                  <span>
                    {{
                      valor(
                        avaliacao.nomeAvaliador,
                        "Responsável não informado"
                      )
                    }}
                  </span>
                </div>

                <div class="historico-status">
                  <span class="historico-badge" :class="classeStatusHistorico(
                    avaliacao.decisaoTomada
                  )
                    ">
                    <i :class="iconeHistorico(
                      avaliacao.decisaoTomada
                    )
                      " aria-hidden="true"></i>

                    {{ traduzirStatus(avaliacao.decisaoTomada) }}
                  </span>
                </div>

                <div v-if="avaliacao.comentario" class="historico-comentario">
                  <span>
                    Comentário
                  </span>

                  <p>
                    {{ avaliacao.comentario }}
                  </p>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="historico-vazio">
            <div class="historico-vazio-icone">
              <i class="fa-regular fa-clock" aria-hidden="true"></i>
            </div>

            <h4>
              Nenhum progresso registrado
            </h4>

            <p>
              Ainda não existem movimentações registradas
              para esta solicitação
            </p>
          </div>
        </div>


      </div>
    </div>

    <div v-if="modalDecisao.aberto" class="overlay" @click.self="fecharDecisao">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-label">{{ modalDecisao.tipo === 'cancelar' ? 'Cancelar parceria' : 'Rejeitar parceria'
          }}</span>

          <button type="button" class="btn-fechar-modal" aria-label="Fechar" :disabled="processandoDecisao"
            @click="fecharDecisao">
            ×
          </button>
        </div>

        <div class="modal-body">
          <p>
            {{
              modalDecisao.tipo === 'cancelar'
                ? 'Você está cancelando (interrompendo) a parceria já formalizada:'
                : 'Você está rejeitando a solicitação de parceria:'
            }}
          </p>

          <p class="modal-destaque">
            <strong>
              {{
                parceria?.nomeAcao ||
                parceria?.tituloAcao ||
                "Solicitação de parceria"
              }}
            </strong>
          </p>

          <div class="input-group">
            <label for="motivo-rejeicao">
              {{ modalDecisao.tipo === 'cancelar' ? 'Motivo do cancelamento:' : 'Motivo do deferimento:' }}
              <span class="campo-obrigatorio">*</span>
            </label>

            <textarea id="motivo-rejeicao" v-model="motivoRejeicao" rows="5" placeholder="Descreva o motivo..."
              :disabled="processandoDecisao"></textarea>
          </div>

          <p v-if="erroDecisao" class="msg-erro">
            {{ erroDecisao }}
          </p>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancelar" :disabled="processandoDecisao" @click="fecharDecisao">
            <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>

            Voltar
          </button>

          <button type="button" class="btn-confirmar btn-confirmar-recusar" :disabled="processandoDecisao ||
            !motivoRejeicao.trim()
            " @click="iniciarConfirmacaoSenha">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>

            {{ modalDecisao.tipo === 'cancelar' ? 'Cancelar parceria' : 'Rejeitar' }}
          </button>
        </div>
      </div>
    </div>


    <div v-if="modalAprovacao.aberto" class="overlay" @click.self="fecharAprovacao">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-label"> Aprovar parceria</span>


          <button type="button" class="btn-fechar-modal" aria-label="Fechar" :disabled="processandoDecisao"
            @click="fecharAprovacao">
            ×
          </button>
        </div>

        <div class="modal-body">
          <p>
            Você está aprovando a solicitação de parceria:
          </p>

          <p class="modal-destaque">
            <strong>
              {{
                parceria?.nomeAcao ||
                parceria?.tituloAcao ||
                "Solicitação de parceria"
              }}
            </strong>
          </p>

          <div class="input-group">
            <label for="observacao-aprovacao">
              Observação da análise:
              <span class="campo-opcional">
                (opcional)
              </span>
            </label>

            <textarea id="observacao-aprovacao" v-model="observacaoAprovacao" rows="4"
              placeholder="Registre uma observação para o solicitante..." :disabled="processandoDecisao"></textarea>
          </div>

          <p v-if="erroDecisao" class="msg-erro">
            {{ erroDecisao }}
          </p>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancelar" :disabled="processandoDecisao" @click="fecharAprovacao">
            <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>

            Voltar
          </button>

          <button type="button" class="btn-confirmar btn-confirmar-aprovar" :disabled="processandoDecisao"
            @click="iniciarConfirmacaoSenhaAprovacao">
            <i class="fa-solid fa-check" aria-hidden="true"></i>

            Aprovar
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CONFIRMAÇÃO DE EDIÇÃO COORDENADOR -->
    <div v-if="modalConfirmarEdicao.visivel" class="overlay" @click.self="fecharConfirmacaoEdicaoCoordenador">
      <div class="modal-confirmar-edicao" @click.stop>
        <div class="modal-header">
          <span class="modal-label">
            <i class="fa-solid fa-triangle-exclamation" aria-hidden="true" style="margin-right: 8px;"></i>
            Atenção: Edição de solicitação
          </span>
          <button type="button" class="btn-fechar-modal" aria-label="Fechar"
            @click="fecharConfirmacaoEdicaoCoordenador">
            ×
          </button>
        </div>

        <div class="modal-body">
          <p>
            Ao editar esta solicitação, o status dela voltará para <strong>'Em edição'</strong> e ela precisará ser
            <strong>reenviada para análise da PREX</strong> após as alterações.
          </p>

          <div v-if="parceria" class="modal-destaque-edicao">
            <span class="destaque-rotulo">Ação vinculada:</span>
            <strong class="destaque-nome">
              {{
                parceria.nomeAcao ||
                parceria.tituloAcao ||
                "Solicitação de parceria"
              }}
            </strong>
          </div>

          <p class="pergunta-edicao">
            Deseja continuar para a edição?
          </p>
        </div>

        <div class="modal-footer-edicao">
          <button type="button" class="btn-cancelar-edicao" @click="fecharConfirmacaoEdicaoCoordenador">
            Cancelar
          </button>
          <button type="button" class="btn-prosseguir-edicao" @click="prosseguirParaEdicaoCoordenador">
            <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
            Continuar para edição
          </button>
        </div>
      </div>
    </div>

    <ModalConfirmarSenha :mostrar="modalSenha.visivel" :carregando="modalSenha.carregando" :erro="modalSenha.erro"
      :mostrar-login="false" descricao="Para confirmar esta ação, informe sua senha do SIGAA."
      @cancelar="fecharModalSenha" @confirmar="onConfirmarSenha" />
  </div>
</template>

<script setup>
import {
  computed,
  defineComponent,
  h,
  onMounted,
  reactive,
  ref,
} from "vue";

import { useRoute, useRouter } from "vue-router";

import Breadcrumb from "@/components/common/Breadcrumb.vue";
import Carregamento from "@/components/common/Carregamento.vue";
import ModalConfirmarSenha from "@/components/usuarios/ModalConfirmarSenha.vue";

import parceriaService from "@/service/parceriaService";

import { STATUS_PARCERIA, STATUS_PARCERIA_LABEL } from "@/constants/parcerias";

import usuariosService from "@/service/usuariosService";

const route = useRoute();
const router = useRouter();

const ehPREX = computed(() => {
  return route.name === "parceria-prex-view";
});

function extrairMensagemErro(error, padrao = "Ocorreu um erro inesperado.") {
  const dados = error?.response?.data || error?.data;
  const mensagem = dados?.message || error?.message || padrao;
  const errosPorCampo = Array.isArray(dados?.errors)
    ? dados.errors
      .map(({ field, message }) =>
        [field, message].filter(Boolean).join(": ")
      )
      .filter(Boolean)
      .join(" • ")
    : "";
  return errosPorCampo ? `${mensagem} — ${errosPorCampo}` : mensagem;
}

async function aprovarParceria(id, observacao = "") {
  await parceriaService.alterarStatus(
    id,
    STATUS_PARCERIA.PARCERIA_FORMALIZADA,
    observacao || "Parceria aprovada e formalizada"
  );
}

async function reprovarParceria(id, motivo) {
  await parceriaService.rejeitar(id, motivo);
}

async function marcarComoEmAnalise(id) {
  await parceriaService.alterarStatus(
    id,
    STATUS_PARCERIA.EM_ANALISE,
    "Análise iniciada"
  );
}

async function avancarParaArticulacao(id, observacao = "") {
  await parceriaService.alterarStatus(
    id,
    STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO,
    observacao || "Avançado para articulação e formalização"
  );
}

async function avancarParaReitoria(id, observacao = "") {
  await parceriaService.alterarStatus(
    id,
    STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA,
    observacao || "Encaminhado para aprovação da reitoria"
  );
}

async function cancelarParceria(id, motivo = "") {
  await parceriaService.alterarStatus(
    id,
    STATUS_PARCERIA.CANCELADA,
    motivo || "Parceria cancelada após formalização"
  );
}



const breadcrumbItems = computed(() => {
  if (route.name === "parceria-prex-view") {
    return [
      {
        label: "Início",
        to: "/",
      },
      {
        label: "Central PREX",
        to: "/central-prex",
      },
      {
        label: "Detalhes da solicitação",
      },
    ];
  }

  return [
    {
      label: "Início",
      to: "/",
    },
    {
      tipo: "acao",
      id: route.params.id,
      fallback: "Ação",
      to: `/acao/${route.params.id}`,
    },
    {
      label: "Central da coordenação",
      to: `/acao/${route.params.id}/central-coordenacao`,
    },
    {
      label: "Parcerias",
      to: `/acao/${route.params.id}/central-coordenacao/parcerias`,
    },
    {
      label: "Detalhes da solicitação",
    },
  ];
});

const parceria = ref(null);
const carregando = ref(true);
const erro = ref("");

const etapaAtual = ref(1);
const totalEtapas = 5;

const nomesEtapas = [
  "Identificação",
  "Responsável",
  "Parceria",
  "Proposta",
  "Execução e avaliação",
];

const progresso = computed(() => {
  if (totalEtapas <= 1) {
    return 100;
  }

  return Math.round(
    ((etapaAtual.value - 1) / (totalEtapas - 1)) * 100
  );
});

const feedback = reactive({
  texto: "",
  tipo: "sucesso",
});

const modalHistorico = reactive({
  aberto: false,
});

const modalDecisao = reactive({
  aberto: false,
  tipo: 'rejeitar'
});

const motivoRejeicao = ref("");

const modalAprovacao = reactive({
  aberto: false,
});

const observacaoAprovacao = ref("");

const erroDecisao = ref("");
const processandoDecisao = ref(false);

const modalSenha = reactive({
  visivel: false,
  carregando: false,
  erro: "",
  acaoPendente: null,
});

// Compatibilidade com registros antigos que ainda usam os status legados
const STATUS_LEGADO = {
  PENDENTE: "FORMULARIO_RECEBIDO",
  APROVADA: "PARCERIA_FORMALIZADA",
  REPROVADA: "REJEITADA",
};

function normalizarStatus(status) {
  const s = String(status || "").trim().toUpperCase();
  return STATUS_LEGADO[s] || s;
}

const statusAtual = computed(() => normalizarStatus(parceria.value?.status));

const podeEditarCoordenador = computed(() =>
  [
    STATUS_PARCERIA.EM_EDICAO,
    STATUS_PARCERIA.FORMULARIO_RECEBIDO,
    STATUS_PARCERIA.CANCELADA,
    STATUS_PARCERIA.REJEITADA,
  ].includes(statusAtual.value)
);

const podeRejeitar = computed(() =>
  [
    STATUS_PARCERIA.FORMULARIO_RECEBIDO,
    STATUS_PARCERIA.EM_ANALISE,
    STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO,
    STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA,
  ].includes(statusAtual.value)
);

const podeFormalizar = computed(() =>
  [
    STATUS_PARCERIA.FORMULARIO_RECEBIDO,
    STATUS_PARCERIA.EM_ANALISE,
    STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO,
    STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA,
  ].includes(statusAtual.value)
);


const CampoVisualizacao = defineComponent({
  name: "CampoVisualizacao",

  props: {
    label: {
      type: String,
      required: true,
    },

    valor: {
      default: null,
    },

    classe: {
      type: String,
      default: "",
    },
  },

  setup(props) {
    return () =>
      h(
        "div",
        {
          class: ["campo-visualizacao", props.classe],
        },
        [
          h(
            "label",
            {
              class: "label-campo",
            },
            props.label
          ),

          h(
            "div",
            {
              class: "valor-campo",
            },
            formatarValorComponente(props.valor)
          ),
        ]
      );
  },
});

const CampoTexto = defineComponent({
  name: "CampoTexto",

  props: {
    label: {
      type: String,
      required: true,
    },

    valor: {
      default: null,
    },
  },

  setup(props) {
    return () =>
      h(
        "div",
        {
          class: "campo-texto",
        },
        [
          h(
            "label",
            {
              class: "label-campo",
            },
            props.label
          ),

          h(
            "div",
            {
              class: "valor-texto",
            },
            formatarValorComponente(props.valor)
          ),
        ]
      );
  },
});


async function carregarParceria() {
  carregando.value = true;
  erro.value = "";

  try {
    const id = route.params.parceriaId;

    if (!id) {
      throw new Error("ID da parceria não informado.");
    }

    const response =
      await parceriaService.buscarParceriaPorId(id);

    const dados =
      response?.data?.data ??
      response?.data ??
      response;

    if (
      !dados ||
      typeof dados !== "object" ||
      Array.isArray(dados)
    ) {
      throw new Error(
        "A API não retornou os dados da parceria."
      );
    }

    parceria.value = dados;
  } catch (error) {
    console.error(
      "Erro ao carregar parceria:",
      error
    );

    erro.value =
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro ao carregar os dados da solicitação.";
  } finally {
    carregando.value = false;
  }
}

function avancar() {
  if (etapaAtual.value < totalEtapas) {
    etapaAtual.value++;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function voltar() {
  if (etapaAtual.value > 1) {
    etapaAtual.value--;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function voltarParaParcerias() {
  if (route.name === "parceria-prex-view") {
    router.push({ name: "central-prex", query: { tab: "parcerias" } });
  } else if (route.name === "DetalhesParceria") {
    router.push({ name: "parcerias-central-coordenacao", params: { id: route.params.id } });
  } else {
    router.push({ name: "parcerias" });
  }
}

const modalConfirmarEdicao = reactive({
  visivel: false,
});

function abrirConfirmacaoEdicaoCoordenador() {
  modalConfirmarEdicao.visivel = true;
}

function fecharConfirmacaoEdicaoCoordenador() {
  modalConfirmarEdicao.visivel = false;
}

function prosseguirParaEdicaoCoordenador() {
  fecharConfirmacaoEdicaoCoordenador();
  editarSolicitacaoCoordenador();
}

function editarSolicitacaoCoordenador() {
  if (!parceria.value?.id) return;

  const targetAcaoId = parceria.value.acaoExtensaoId || route.params.id;
  const nomeRota =
    parceria.value.tipoParceria === "INTRA_MUROS" ||
      parceria.value.tipoParceria === "INTERNA"
      ? "formulario-parceria-interna"
      : "formulario-parceria-externa";

  router.push({
    name: nomeRota,
    params: {
      id: targetAcaoId,
      acaoId: targetAcaoId,
    },
    query: {
      parceriaId: parceria.value.id,
    },
  });
}

async function enviarSolicitacaoCoordenador() {
  if (!parceria.value?.id || processandoDecisao.value) return;

  processandoDecisao.value = true;
  limparFeedback();

  try {
    await parceriaService.enviarParceria(parceria.value.id);
    feedback.tipo = "sucesso";
    feedback.texto = "Solicitação enviada para análise da PREX com sucesso!";
    await carregarParceria();
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (error) {
    console.error("Erro ao enviar solicitação:", error);
    feedback.tipo = "erro";
    feedback.texto = extrairMensagemErro(
      error,
      "Não foi possível enviar a solicitação para análise."
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  } finally {
    processandoDecisao.value = false;
  }
}

async function excluirSolicitacaoCoordenador() {
  if (!parceria.value?.id || processandoDecisao.value) return;

  const nome = parceria.value.nomeAcao || parceria.value.tituloAcao || "esta solicitação";
  if (
    !window.confirm(
      `Deseja realmente excluir a solicitação de parceria "${nome}"? Esta ação não poderá ser desfeita.`
    )
  ) {
    return;
  }

  processandoDecisao.value = true;
  limparFeedback();

  try {
    await parceriaService.excluirParceria(parceria.value.id);
    voltarParaParcerias();
  } catch (error) {
    console.error("Erro ao excluir solicitação:", error);
    feedback.tipo = "erro";
    feedback.texto = extrairMensagemErro(
      error,
      "Não foi possível excluir a solicitação de parceria."
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  } finally {
    processandoDecisao.value = false;
  }
}

function abrirHistorico() {
  modalHistorico.aberto = true;
}

function fecharHistorico() {
  modalHistorico.aberto = false;
}

function tituloHistorico(decisao) {
  const titulos = {
    EM_EDICAO: "Solicitação em edição",
    FORMULARIO_RECEBIDO: "Formulário recebido",
    EM_ANALISE: "Solicitação encaminhada para análise",
    EM_ARTICULACAO_E_FORMALIZACAO: "Em articulação",
    AGUARDANDO_APROVACAO_REITORIA: "Aguardando aprovação da reitoria",
    PARCERIA_FORMALIZADA: "Parceria formalizada",
    REJEITADA: "Parceria indeferida",
    CANCELADA: "Parceria cancelada",
  };

  const chave = normalizarStatus(decisao);

  return (
    titulos[chave] ||
    traduzirStatus(decisao)
  );
}

function iconeHistorico(decisao) {
  const icones = {
    EM_EDICAO: "fa-solid fa-pen-to-square",
    FORMULARIO_RECEBIDO: "fa-solid fa-file-circle-plus",
    EM_ANALISE: "fa-solid fa-magnifying-glass",
    EM_ARTICULACAO_E_FORMALIZACAO: "fa-solid fa-handshake",
    AGUARDANDO_APROVACAO_REITORIA: "fa-solid fa-building-columns",
    PARCERIA_FORMALIZADA: "fa-solid fa-circle-check",
    REJEITADA: "fa-solid fa-circle-xmark",
    CANCELADA: "fa-solid fa-ban",
  };

  return (
    icones[normalizarStatus(decisao)] ||
    "fa-solid fa-clock-rotate-left"
  );
}


function classeIconeHistorico(decisao) {
  const classes = {
    EM_EDICAO: "historico-edicao",
    FORMULARIO_RECEBIDO: "historico-pendente",
    PENDENTE: "historico-pendente",

    EM_ANALISE: "historico-analise",
    AGUARDANDO_AJUSTES: "historico-ajustes",

    EM_ARTICULACAO_E_FORMALIZACAO:
      "historico-articulacao",

    AGUARDANDO_APROVACAO_REITORIA:
      "historico-reitoria",

    PARCERIA_FORMALIZADA: "historico-aprovada",
    APROVADA: "historico-aprovada",

    REJEITADA: "historico-reprovada",
    REPROVADA: "historico-reprovada",

    CANCELADA: "historico-cancelada",
  };

  return classes[normalizarStatus(decisao)] || "historico-padrao";
}

function classeStatusHistorico(decisao) {
  return classeIconeHistorico(decisao);
}


function solicitarSenha(acao) {
  if (!parceria.value?.id) {
    return;
  }

  modalSenha.acaoPendente = acao;
  modalSenha.erro = "";
  modalSenha.visivel = true;
}

function abrirDecisaoRejeitar() {
  modalDecisao.tipo = "rejeitar";
  modalDecisao.aberto = true;
  motivoRejeicao.value = "";
  erroDecisao.value = "";
}

function abrirDecisaoCancelar() {
  modalDecisao.tipo = "cancelar";
  modalDecisao.aberto = true;
  motivoRejeicao.value = "";
  erroDecisao.value = "";
}

function fecharDecisao() {
  if (processandoDecisao.value) {
    return;
  }

  modalDecisao.aberto = false;
  motivoRejeicao.value = "";
  erroDecisao.value = "";
}

function abrirAprovacao() {
  modalAprovacao.aberto = true;
  observacaoAprovacao.value = "";
  erroDecisao.value = "";
}

function fecharAprovacao() {
  if (processandoDecisao.value) {
    return;
  }

  modalAprovacao.aberto = false;
  observacaoAprovacao.value = "";
  erroDecisao.value = "";
}

function iniciarConfirmacaoSenha() {
  if (!motivoRejeicao.value.trim()) {
    return;
  }

  modalDecisao.aberto = false;
  // "rejeitar" ou "cancelar", conforme o modal aberto
  modalSenha.acaoPendente = modalDecisao.tipo;
  modalSenha.erro = "";
  modalSenha.visivel = true;
}

function iniciarConfirmacaoSenhaAprovacao() {
  modalAprovacao.aberto = false;
  modalSenha.acaoPendente = "aprovar";
  modalSenha.erro = "";
  modalSenha.visivel = true;
}

function fecharModalSenha() {
  if (modalSenha.carregando) {
    return;
  }

  const acao = modalSenha.acaoPendente;

  modalSenha.visivel = false;
  modalSenha.erro = "";

  if (acao === "rejeitar" || acao === "cancelar") {
    modalDecisao.aberto = true;
    return;
  }

  if (acao === "aprovar") {
    modalAprovacao.aberto = true;
  }
}

async function onConfirmarSenha(senhaOrObj) {
  const senha =
    typeof senhaOrObj === "object" ? senhaOrObj.senha : senhaOrObj;

  if (!senha) {
    return;
  }

  modalSenha.carregando = true;
  modalSenha.erro = "";

  try {
    const senhaValida = await usuariosService.verificarSenha(senhaOrObj);

    if (!senhaValida) {
      modalSenha.erro = "Senha incorreta. Tente novamente.";
      return;
    }

    const acao = modalSenha.acaoPendente;
    const parceriaId = parceria.value?.id;

    if (!parceriaId) {
      throw new Error("ID da parceria não encontrado.");
    }

    modalSenha.visivel = false;
    processandoDecisao.value = true;

    if (acao === "analisar") {
      await marcarComoEmAnalise(parceriaId);
      feedback.texto =
        "Análise iniciada. Você foi registrado como analista responsável.";
      feedback.tipo = "sucesso";
      await carregarParceria();
    } else if (acao === "aprovar") {
      await aprovarParceria(parceriaId, observacaoAprovacao.value);
      feedback.texto = "Parceria formalizada com sucesso.";
      feedback.tipo = "sucesso";
      fecharAprovacao();
      await carregarParceria();
    } else if (acao === "rejeitar") {
      await reprovarParceria(parceriaId, motivoRejeicao.value);
      feedback.texto = "Parceria indeferida.";
      feedback.tipo = "sucesso";
      fecharDecisao();
      await carregarParceria();
    } else if (acao === "articular") {
      await avancarParaArticulacao(parceriaId);
      feedback.texto = "Avançado para etapa de articulação.";
      feedback.tipo = "sucesso";
      await carregarParceria();
    } else if (acao === "reitoria") {
      await avancarParaReitoria(parceriaId);
      feedback.texto = "Enviado para aprovação da reitoria.";
      feedback.tipo = "sucesso";
      await carregarParceria();
    } else if (acao === "cancelar") {
      await cancelarParceria(parceriaId, motivoRejeicao.value);
      feedback.texto = "Parceria cancelada com sucesso.";
      feedback.tipo = "sucesso";
      fecharDecisao();
      await carregarParceria();
    }
  } catch (error) {
    console.error("Erro ao confirmar ação da PREX:", error);
    if (!modalSenha.visivel) {
      feedback.tipo = "erro";
      feedback.texto = extrairMensagemErro(
        error,
        "Não foi possível atualizar o status da parceria."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      modalSenha.erro = extrairMensagemErro(
        error,
        "Não foi possível validar suas credenciais. Tente novamente."
      );
    }
  } finally {
    processandoDecisao.value = false;
    modalSenha.carregando = false;
    modalSenha.acaoPendente = null;
  }
}

function limparFeedback() {
  feedback.texto = "";
}

function valor(
  valorCampo,
  fallback = "Não informado"
) {
  if (
    valorCampo === null ||
    valorCampo === undefined ||
    valorCampo === ""
  ) {
    return fallback;
  }

  return valorCampo;
}

function formatarValorComponente(
  valorCampo
) {
  if (
    valorCampo === null ||
    valorCampo === undefined ||
    valorCampo === ""
  ) {
    return "Não informado";
  }

  if (
    typeof valorCampo === "boolean"
  ) {
    return valorCampo
      ? "Sim"
      : "Não";
  }

  if (Array.isArray(valorCampo)) {
    if (valorCampo.length === 0) {
      return "Não informado";
    }

    const valores =
      valorCampo
        .filter(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== ""
        )
        .map((item) =>
          typeof item === "object"
            ? Object.values(item).join(
              ", "
            )
            : String(item)
        );

    return valores.length
      ? valores.join(", ")
      : "Não informado";
  }

  if (
    typeof valorCampo === "object"
  ) {
    const valores =
      Object.values(valorCampo)
        .filter(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== ""
        )
        .map((item) =>
          String(item)
        );

    return valores.length
      ? valores.join(", ")
      : "Não informado";
  }

  return String(valorCampo);
}

function formatarLista(lista) {
  if (
    lista === null ||
    lista === undefined
  ) {
    return "Não informado";
  }

  if (Array.isArray(lista)) {
    if (lista.length === 0) {
      return "Não informado";
    }

    const valores =
      lista
        .filter(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== ""
        )
        .map((item) =>
          typeof item === "object"
            ? Object.values(item).join(
              ", "
            )
            : String(item)
        );

    return valores.length
      ? valores.join(", ")
      : "Não informado";
  }

  if (
    typeof lista === "object"
  ) {
    const valores =
      Object.values(lista)
        .filter(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== ""
        )
        .map((item) =>
          String(item)
        );

    return valores.length
      ? valores.join(", ")
      : "Não informado";
  }

  if (
    String(lista).trim() === ""
  ) {
    return "Não informado";
  }

  return String(lista);
}

function formatarDataHora(data) {
  if (!data) {
    return "Não informado";
  }

  try {
    const dataFormatada =
      new Date(data);

    if (
      Number.isNaN(
        dataFormatada.getTime()
      )
    ) {
      return String(data);
    }

    return dataFormatada.toLocaleString(
      "pt-BR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return String(data);
  }
}

function traduzirTipoParceria(tipo) {
  const tipos = {
    EXTERNA: "Parceria externa",
    INTRA_MUROS: "Parceria interna",
    INTERNA: "Parceria interna",
  };

  return tipos[tipo] || valor(tipo);
}

function traduzirStatus(status) {
  return (
    STATUS_PARCERIA_LABEL[normalizarStatus(status)] ||
    valor(status)
  );
}


function classeStatus(status) {
  const classes = {
    // Entrada da solicitação
    EM_EDICAO: "status-em_edicao",
    FORMULARIO_RECEBIDO: "status-formulario_recebido",
    PENDENTE: "status-formulario_recebido",

    // Análise
    EM_ANALISE: "status-em_analise",
    AGUARDANDO_AJUSTES: "status-aguardando_ajustes",

    // Articulação e formalização
    EM_ARTICULACAO_E_FORMALIZACAO:
      "status-em_articulacao_e_formalizacao",

    AGUARDANDO_APROVACAO_REITORIA:
      "status-aguardando_aprovacao_reitoria",

    // Resultado positivo
    PARCERIA_FORMALIZADA: "status-parceria_formalizada",
    APROVADA: "status-parceria_formalizada",

    // Resultado negativo
    REJEITADA: "status-rejeitada",
    REPROVADA: "status-rejeitada",

    // Cancelamento
    CANCELADA: "status-cancelada",
  };

  return classes[normalizarStatus(status)] || "status-padrao";
}


onMounted(() => {
  carregarParceria();
});
</script>

<style scoped>
#parceria-view {
  min-height: calc(100vh - 130px);
  background: var(--branco, #ffffff);
}

.formulario-container {
  width: 100%;
  max-width: 1200px;
  margin: 25px auto 0;
}

.cabecalho {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--cinza-claro, #e5e5e5);
}

.cabecalho-informacoes {
  flex: 1;
  min-width: 0;
}

.tag {
  display: inline-block;
  margin-bottom: 10px;
  color: var(--roxo-primario, #6c2bd9);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}

.cabecalho h1 {
  margin: 0 0 12px;
  color: var(--preto, #111111);
  font-size: 28px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.cabecalho p {
  max-width: 760px;
  margin: 0 0 12px;
  color: var(--cinza-escuro, #4a4a4a);
  font-size: 15px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.tempo {
  display: inline-block;
  color: var(--cinza-medio, #777777);
  font-size: 13px;
}

.status-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.status-label {
  color: var(--roxo-primario);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}

.status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}

.status-em_edicao {
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fdba74;
}

.status-formulario_recebido {
  color: var(--branco);
  background: var(--azul-claro);
}


/* Em andamento */
.status-em_analise {
  color: var(--branco);
  background: var(--azul-escuro);
}

.status-aguardando_ajustes {
  color: #9a3412;
  background: #fff7ed;
  border-color: #fdba74;
}

.status-em_articulacao_e_formalizacao {
  color: var(--branco);
  background: var(--verde);
}

.status-aguardando_aprovacao_reitoria {
  color: var(--branco);
  background: var(--laranja-escuro);
}


.status-parceria_formalizada {
  color: var(--branco);
  background: var(--roxo-medio);
}

/* Resultados negativos */
.status-rejeitada {
  color: var(--branco);
  background: var(--vermelho);
}

/* Cancelamento */
.status-cancelada {
  color: var(--branco);
  background: var(--cinza-escuro);

}

.bloco-justificativa-status {
  margin: 18px 0 0;
  padding: 14px 18px;
  border-left: 4px solid var(--vermelho, #dc2626);
  border-radius: 8px;
  background: #fef2f2;
  color: #7f1d1d;
}

.bloco-justificativa-status strong {
  display: block;
  margin-bottom: 4px;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bloco-justificativa-status p {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.progresso-container {
  margin: 35px 0 45px;
}

.progresso-topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: var(--cinza-escuro, #4a4a4a);
  font-size: 13px;
  font-weight: 600;
}

.barra-progresso {
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 10px;
  background: var(--cinza-claro, #e8e8e8);
}

.barra-progresso-preenchida {
  height: 100%;
  border-radius: 10px;
  background: var(--roxo-medio);
  transition: width 0.3s ease;
}

.etapas {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-top: 20px;
}

.etapa {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  color: #999999;
  text-align: center;
}

.etapa>span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eeeeee;
  font-size: 12px;
  font-weight: 700;
}

.etapa small {
  font-size: 11px;
  line-height: 1.3;
}

.etapa.ativa {
  color: var(--roxo-primario, #6c2bd9);
}

.etapa.ativa>span {
  background: var(--roxo-primario, #6c2bd9);
  color: var(--branco);
}

.etapa.concluida {
  color: #555555;
}

.etapa.concluida>span {
  background: #ded3f7;
  color: var(--roxo-primario, #6c2bd9);
}

.etapa-conteudo {
  min-width: 0;
}

.secao-titulo {
  margin-bottom: 32px;
}

.secao-titulo>span {
  display: block;
  margin-bottom: 8px;
  color: var(--roxo-primario, #6c2bd9);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
}

.secao-titulo h2 {
  margin: 0 0 10px;
  color: var(--preto, #111111);
  font-size: 23px;
  line-height: 1.3;
}

.secao-titulo p {
  max-width: 760px;
  margin: 0;
  color: var(--cinza-escuro, #555555);
  font-size: 14px;
  line-height: 1.7;
}

.campos-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 22px;
  width: 100%;
  min-width: 0;
}

.campo-visualizacao {
  width: 100%;
  min-width: 0;
}

.campo-visualizacao :deep(.label-campo),
.campo-texto .label-campo {
  display: block;
  margin: 0 0 7px;
  color: var(--roxo-primario);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.7px;
  line-height: 1.3;
  text-transform: uppercase;
}

.valor-campo,
.valor-texto {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  background: #fbfbfc;
  color: #222222;
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}

.valor-campo {
  display: block;
  min-height: 48px;
  padding: 13px 15px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.55;
}

.valor-texto {
  min-height: 105px;
  padding: 17px 18px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.8;
  white-space: pre-wrap;
}

.campo-longo {
  grid-column: 1 / -1;
}

.campo-texto {
  width: 100%;
  min-width: 0;
  margin-top: 24px;
}

.secao-titulo+.campo-texto {
  margin-top: 0;
}

.bloco-destaque {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 30px;
  padding: 18px 20px;
  border-left: 4px solid var(--roxo-primario, #6c2bd9);
  border-radius: 6px;
  background: #f7f4fc;
}

.bloco-destaque span {
  color: #777777;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
}

.bloco-destaque strong {
  color: var(--roxo-primario, #6c2bd9);
  font-size: 16px;
  font-weight: 700;
}

.subsecao {
  margin: 38px 0 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eeeeee;
}

.subsecao h3 {
  margin: 0;
  color: #222222;
  font-size: 16px;
  font-weight: 700;
}

.mensagem-erro {
  max-width: 700px;
  margin: 80px auto;
  padding: 35px;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  text-align: center;
}

.mensagem-erro h2 {
  margin: 0 0 10px;
  color: #222222;
}

.mensagem-erro p {
  margin: 0 0 25px;
  color: #666666;
}

.navegacao {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.navegacao .btn-continuar,
.navegacao .btn-enviar {
  margin-left: auto;
}

.btn-voltar,
.btn-continuar,
.btn-analisar-prex,
.btn-rejeitar-prex,
.btn-aprovar-prex,
.btn-editar-coordenador,
.btn-enviar,
.btn-historico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  color: var(--branco);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease;
}

.btn-continuar,
.btn-enviar {
  padding-inline: 20px;
  background: var(--roxo-primario);
}

.btn-voltar {
  background: var(--cinza-escuro);
}

.btn-historico {
  background: var(--roxo-primario);
}

.btn-editar-coordenador {
  background: var(--azul-escuro, #1e40af);
}

.btn-analisar-prex {
  background: var(--azul-escuro);
}

.btn-rejeitar-prex {
  background: var(--vermelho-escuro);
}

.btn-aprovar-prex {
  background: var(--verde-escuro);
}

.btn-voltar:hover:not(:disabled) {
  background: var(--cinza-medio);
}

.btn-historico:hover:not(:disabled) {
  background: var(--roxo-medio);
}

.btn-editar-coordenador:hover:not(:disabled) {
  background: var(--azul-claro, #2563eb);
}

.btn-continuar:hover:not(:disabled),
.btn-enviar:hover:not(:disabled) {
  background: var(--roxo-medio);
}

.btn-analisar-prex:hover:not(:disabled) {
  background: var(--azul-claro);
}

.btn-rejeitar-prex:hover:not(:disabled) {
  background: var(--vermelho);
}

.btn-aprovar-prex:hover:not(:disabled) {
  background: var(--verde);
}

.btn-voltar:disabled,
.btn-continuar:disabled,
.btn-analisar-prex:disabled,
.btn-rejeitar-prex:disabled,
.btn-aprovar-prex:disabled,
.btn-editar-coordenador:disabled,
.btn-enviar:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}



.modal-historico {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  max-height: min(850px, 80vh);
  overflow: hidden;
  border-radius: 14px;
  box-shadow: 0 20px 50px hsla(0, 0%, 0%, 0.25);
}

.modal-historico .modal-header {
  flex-shrink: 0;
}

.historico-body {
  background: var(--branco);
  min-height: 0;
  padding: 1rem;
  overflow-y: auto;
}

.linha-do-tempo {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.linha-do-tempo::before {
  position: absolute;
  top: 22px;
  bottom: 22px;
  left: 20px;
  width: 2px;
  background: #e5e7eb;
  content: "";
}

.historico-item {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 16px;
  padding-bottom: 28px;
}

.historico-item:last-child {
  padding-bottom: 0;
}

.historico-icone {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 4px solid var(--branco);
  border-radius: 50%;
  box-sizing: border-box;
  font-size: 15px;
}

.historico-ajustes {
  color: #9a3412;
  background: #fff7ed;
}

.historico-analise {
  color: var(--branco);
  background: var(--azul-escuro);
}

.historico-articulacao {
  color: var(--branco);
  background: var(--verde);
}

.historico-reitoria {
  color: var(--branco);
  background: var(--laranja-escuro);
}

.historico-cancelada {
  color: var(--branco);
  background: var(--cinza-escuro);
}

.historico-aprovada {
  color: var(--branco);
  background: var(--roxo-medio);
}

.historico-badge.historico-ajustes {
  color: #9a3412;
  background: #fff7ed;
}

.historico-badge.historico-articulacao {
  color: var(--branco);
  background: var(--verde);
}

.historico-badge.historico-reitoria {
  color: var(--branco);
  background: var(--laranja-escuro);
}


.historico-badge.historico-cancelada {
  color: var(--branco);
  background: var(--cinza-escuro);
}


.historico-conteudo {
  min-width: 0;
  padding: 3px 0 0;
}

.historico-topo {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.historico-etapa {
  display: block;
  margin-bottom: 5px;
  color: var(--roxo-primario);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.8px;
}

.historico-topo h4 {
  margin: 0;
  color: #222222;
  font-size: 16px;
  line-height: 1.4;
}

.historico-data {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  color: #777777;
  font-size: 11px;
  white-space: nowrap;
}

.historico-avaliador {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  color: #666666;
  font-size: 13px;
}

.historico-avaliador i {
  color: #999999;
  font-size: 11px;
}

.historico-status {
  margin-top: 12px;
}

.historico-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 700;
}

.historico-badge.historico-pendente {
  background: #fff4d6;
  color: #9a6a00;
}

.historico-badge.historico-analise {
  background: var(--azul-escuro);
  color: var(--branco);
}

.historico-badge.historico-aprovada {
  background: var(--roxo-medio);
  color: var(--branco);
}

.historico-badge.historico-reprovada {
  background: var(--vermelho);
  color: var(--branco);
}

.historico-comentario {
  margin-top: 14px;
  padding: 12px 14px;
  border-left: 3px solid #ddd5ee;
  border-radius: 5px;
  background: #faf9fc;
}

.historico-comentario span {
  display: block;
  margin-bottom: 5px;
  color: #777777;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.historico-comentario p {
  margin: 0;
  color: #444444;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.historico-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 30px;
  text-align: center;
}

.historico-vazio-icone {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-bottom: 18px;
  border-radius: 50%;
  background: #f1f1f1;
  color: #888888;
  font-size: 22px;
}

.historico-vazio h4 {
  margin: 0 0 8px;
  color: #333333;
  font-size: 16px;
}

.historico-vazio p {
  max-width: 380px;
  margin: 0;
  color: #777777;
  font-size: 13px;
  line-height: 1.6;
}

.historico-footer {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: 14px 24px;
  border-top: 1px solid #eeeeee;
  background: #fafafa;
}

.btn-fechar-historico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 17px;
  border: none;
  border-radius: 7px;
  background: var(--cinza-escuro);
  color: var(--branco);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-fechar-historico:hover {
  background: var(--cinza-medio);
}

.modal-content {
  width: 100%;
  max-width: 450px;
  padding: 0;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 25px hsla(0, 0%, 0%, 0.2);
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 1rem;
  background: var(--branco);
}

.modal-body p {
  margin: 0;
  color: var(--preto);
  font-size: 0.95rem;
  line-height: 1.5;
}

.modal-destaque {
  padding: 12px 14px;
  border-left: 4px solid var(--roxo-primario);
  border-radius: 6px;
  background: #f7f4fc;
  color: var(--cinza-escuro) !important;
  overflow-wrap: anywhere;
}

.modal-destaque strong {
  color: var(--roxo-primario);
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  color: var(--cinza-escuro);
  font-size: 0.9rem;
  font-weight: 600;
}

.campo-obrigatorio {
  color: var(--vermelho);
}

.campo-opcional {
  color: #999999;
  font-weight: 400;
}

.input-group textarea {
  width: 100%;
  min-height: 100px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid var(--cinza-claro);
  border-radius: 6px;
  background: var(--branco);
  color: var(--cinza-escuro);
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.input-group textarea:focus {
  border-color: var(--roxo-primario);
  box-shadow: 0 0 0 2px hsla(258, 53%, 28%, 0.1);
}

.input-group textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.msg-erro,
.erro-decisao {
  margin: 0 !important;
  color: var(--vermelho) !important;
  font-size: 0.85rem !important;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 1rem;
  background: var(--branco);
}

.modal-footer button {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s ease,
    opacity 0.2s ease;
}

.modal-footer .btn-cancelar {
  background: var(--cinza-escuro);
  color: var(--branco);
}

.modal-footer .btn-cancelar:hover:not(:disabled) {
  background: var(--cinza-medio);
}

.modal-footer .btn-confirmar {
  border: none;
  color: var(--branco);
}

.btn-confirmar-recusar {
  background: var(--vermelho);
}

.btn-confirmar-recusar:hover:not(:disabled) {
  background: var(--vermelho-escuro);
}

.btn-confirmar-aprovar {
  background: var(--verde-escuro);
}

.btn-confirmar-aprovar:hover:not(:disabled) {
  background: var(--verde);
}

.modal-footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}



@media (max-width: 800px) {
  #parceria-view {
    padding: 25px 5%;
  }

  .cabecalho {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .status-container {
    width: 100%;
  }

  .campos-grid {
    grid-template-columns: 1fr;
  }

  .campo-longo {
    grid-column: auto;
  }
}

@media (max-width: 600px) {
  .navegacao {
    flex-wrap: wrap;
  }

  .navegacao .btn-continuar,
  .navegacao .btn-enviar {
    margin-left: 0;
  }

  .navegacao button {
    flex: 1;
  }

  .modal-historico {
    max-height: 94vh;
  }

  .historico-body {
    padding: 20px 18px;
  }

  .historico-topo {
    flex-direction: column;
    gap: 8px;
  }

  .historico-data {
    white-space: normal;
  }
}

@media (max-width: 500px) {
  #parceria-view {
    padding: 20px 4%;
  }

  .cabecalho h1 {
    font-size: 23px;
  }

  .cabecalho p {
    font-size: 14px;
  }

  .etapas {
    grid-template-columns: repeat(2, 1fr);
  }

  .progresso-container {
    margin-bottom: 30px;
  }

  .navegacao {
    flex-direction: column;
  }

  .navegacao button,
  .btn-voltar,
  .btn-continuar,
  .btn-enviar,
  .btn-historico {
    width: 100%;
    margin-left: 0;
  }

  .historico-item {
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 12px;
  }

  .historico-icone {
    width: 36px;
    height: 36px;
    font-size: 13px;
  }

  .linha-do-tempo::before {
    left: 17px;
  }

  .historico-topo h4 {
    font-size: 15px;
  }

  .modal-content {
    max-width: 100%;
  }

  .modal-header {
    padding: 14px 18px;
  }

  .modal-body {
    padding: 20px 18px;
  }

  .modal-footer {
    padding: 14px 18px;
  }

  .modal-footer button {
    flex: 1;
  }

  .historico-footer {
    padding: 12px 18px;
  }

  .btn-fechar-historico {
    width: 100%;
  }
}

.feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 16px 0;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.feedback.sucesso {
  background: #e8f7ee;
  color: #1e7a45;
  border: 1px solid #b8e4c9;
}

.feedback.erro {
  background: #fdecec;
  color: #a12626;
  border: 1px solid #f3c0c0;
}

.feedback button {
  background: transparent;
  border: none;
  color: inherit;
  font-size: 1.1rem;
  cursor: pointer;
}

.btn-submeter-analise {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  color: var(--branco);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: var(--laranja-escuro, #d97706);
  transition: background 0.2s ease, opacity 0.2s ease;
}

.btn-submeter-analise:hover:not(:disabled) {
  background: var(--laranja-medio, #b45309);
}

.btn-submeter-analise:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-formalizar-destaque {
  background: #16a34a !important;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.35);
}

.btn-formalizar-destaque:hover:not(:disabled) {
  background: #15803d !important;
}

.status-em_edicao {
  background-color: #fef3c7 !important;
  color: #92400e !important;
  border: 1px solid #fcd34d !important;
}

.historico-edicao {
  background: #fef3c7 !important;
  color: #92400e !important;
}


.modal-confirmar-edicao {
  width: 100%;
  max-width: 480px;
  background: var(--branco);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  animation: fadeInModal 0.2s ease-out;
}

@keyframes fadeInModal {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-confirmar-edicao .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--roxo-primario);
  color: var(--branco);
}

.modal-confirmar-edicao .modal-label {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--branco);
}

.modal-confirmar-edicao .btn-fechar-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  color: var(--branco);
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;
}

.modal-confirmar-edicao .btn-fechar-modal:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-confirmar-edicao .modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  background: var(--branco);
}

.modal-confirmar-edicao .modal-body p {
  margin: 0;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.55;
}

.modal-destaque-edicao {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-left: 4px solid var(--roxo-primario);
  border-radius: 6px;
  background: #f7f4fc;
}

.destaque-rotulo {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.destaque-nome {
  font-size: 0.95rem;
  color: var(--roxo-primario);
  word-break: break-word;
}

.pergunta-edicao {
  font-weight: 600;
  color: #111827 !important;
}

.modal-footer-edicao {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.btn-cancelar-edicao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: var(--branco);
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancelar-edicao:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.btn-prosseguir-edicao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  background: var(--roxo-primario);
  color: var(--branco);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-prosseguir-edicao:hover {
  background: var(--roxo-medio);
}
</style>
